'use client';
import { useState } from 'react';
import { Check, Circle, CircleDot, ListChecks } from 'lucide-react';
import { interpolate, useTranslation } from '../../lib/i18n';
import { formatDuration } from '../../lib/format';
import { TODO_PREVIEW_ITEMS } from '../../lib/chatPresentation';
import { cn } from '../../lib/utils';
import { ActionMenu } from './ActionMenu';
import type { RailRowProps } from './RailPrimitives';
import { Spinner } from './states';
import { StatusGlyph } from './StatusMark';
import { MarkerIcon } from './shadcn/marker';
import { Marker, MarkerContent } from './shadcn/marker';
import {
  Trace, TraceLine, TraceIcon, TraceTitle, TraceDescription,
  TraceCollapsible, TraceCollapsibleTrigger, TraceCollapsibleContent,
} from './shadcn/trace';

export type TaskStatus = 'pending' | 'in_progress' | 'completed';

export interface TaskListTask {
  id: string;
  label: string;
  status: TaskStatus;
  startedAt?: number;
  durationMs?: number;
  owner?: string;
  blockedBy?: readonly string[];
}

interface TaskControls {
  now: number;
  onStatus?: (task: TaskListTask, status: TaskStatus) => void;
  onOpen?: () => void;
  disabled?: boolean;
}

export interface TaskRowProps extends TaskControls {
  task: TaskListTask;
  density?: 'rail' | 'inline';
  testId?: string;
}

/** The documented AI Elements Queue row, retaining the public task contract and explicit status menu.
 * A row never changes the plan on a stray click: only a menu selection calls onStatus. */
export function TaskRow({ task, density = 'rail', testId = 'task-row', now, onStatus, onOpen, disabled = false }: TaskRowProps) {
  const { t } = useTranslation();
  const done = task.status === 'completed';
  const blockedBy = task.status === 'pending' ? task.blockedBy ?? [] : [];
  const waiting = blockedBy.length > 0
    ? interpolate(t.telemetry.taskWaiting, { ids: blockedBy.map((id) => `#${id}`).join(', ') })
    : null;
  const elapsed = task.status === 'in_progress' && Number.isFinite(task.startedAt)
    ? formatDuration(Math.max(0, now - task.startedAt!))
    : done && Number.isFinite(task.durationMs) ? formatDuration(task.durationMs!) : null;
  const lead = task.status === 'in_progress'
    ? <span data-testid="task-running" className="flex"><Spinner size={density === 'rail' ? 'sm' : 'xs'} tone="text-primary" /></span>
    : done
      ? <StatusGlyph kind="success" tone="success" size={density === 'rail' ? 14 : 11} />
      : <Circle aria-hidden className={cn('shrink-0 text-subtle-foreground', density === 'rail' ? 'size-3.5' : 'size-3')} />;
  const actions: RailRowProps['actions'] = onStatus ? [
    { label: t.tasksModal.statusPending, icon: Circle, disabled, onSelect: () => onStatus(task, 'pending') },
    { label: t.tasksModal.statusInProgress, icon: CircleDot, disabled, onSelect: () => onStatus(task, 'in_progress') },
    { label: t.tasksModal.statusCompleted, icon: Check, disabled, onSelect: () => onStatus(task, 'completed') },
    ...(onOpen ? [{ label: t.telemetry.tasksOpen, icon: ListChecks, onSelect: onOpen }] : []),
  ] : undefined;
  const note = [task.owner, waiting].filter(Boolean).join(', ');
  const ariaLabel = `${t.tasksModal.taskActions}: ${task.label}${note ? ` (${note})` : ''}`;
  const line = (
    <TraceLine status={task.status === 'in_progress' ? 'running' : undefined}
      className={cn('w-full', density === 'rail' ? 'min-h-7 text-conversation' : 'task-row-inline gap-1.5 rounded px-1 py-0.5',
        !done && waiting === null && 'text-foreground')}>
      <TraceIcon>{lead}</TraceIcon>
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <TraceTitle title={task.label} className={cn(task.owner ? 'max-w-[70%] shrink-0' : 'shrink', task.status === 'in_progress' && 'shimmer')}>
          <span data-testid="task-subject" className={done ? 'line-through' : undefined}>{task.label}</span>
        </TraceTitle>
        {task.owner ? <span className="min-w-0 flex-1 truncate text-meta text-muted-foreground">{task.owner}</span> : null}
      </span>
      <TraceDescription className={cn('ml-auto whitespace-nowrap', density === 'rail' && 'text-meta')}>
        {elapsed ? <span data-testid="task-elapsed">{elapsed}</span> : waiting ? <span data-testid="task-waiting">{waiting}</span> : null}
      </TraceDescription>
    </TraceLine>
  );
  return (
    <li data-testid={testId} className="flex min-w-0">
      {actions ? <ActionMenu items={actions} label={ariaLabel} align="left" openOnHover={false}
        className="min-w-0 flex-1"
        triggerClassName="w-full rounded-md text-left transition-colors duration-[var(--motion-fast)] hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent"
        trigger={line} /> : line}
    </li>
  );
}

export interface TaskListProps extends TaskControls {
  tasks: readonly TaskListTask[];
}

/** Queue's Trace composition. Running tasks precede pending ones; completed work stays folded.
 * Existing preview limits, time ownership and plugin-facing props are unchanged. */
export function TaskList({ tasks, ...controls }: TaskListProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const open = [...tasks.filter((task) => task.status === 'in_progress'), ...tasks.filter((task) => task.status === 'pending')];
  const done = tasks.filter((task) => task.status === 'completed');
  const folds = open.length > TODO_PREVIEW_ITEMS + 1;
  const shown = folds && !expanded ? open.slice(0, TODO_PREVIEW_ITEMS) : open;
  return (
    <Trace className="gap-0">
      <ul className="flex min-w-0 flex-col">
        {shown.map((task) => <TaskRow key={task.id} task={task} {...controls} />)}
        {folds ? <li>
          <Marker asChild className="min-h-7 text-conversation">
            <button type="button" data-testid="task-list-more" className="rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
              <MarkerIcon />
              <MarkerContent>{expanded ? t.pills.showLess : interpolate(t.pills.showMore, { n: String(open.length - shown.length) })}</MarkerContent>
            </button>
          </Marker>
        </li> : null}
        {done.length > 0 ? <li className="min-w-0">
          <TraceCollapsible>
            <TraceCollapsibleTrigger data-testid="task-list-done" className="w-full min-h-7 gap-2 [&>[data-slot=trace-icon]]:order-first">
              <TraceLine className="w-full text-conversation">
                <TraceTitle>{t.tasksModal.statusCompleted}</TraceTitle>
                <TraceDescription className="ml-auto text-meta">{done.length}</TraceDescription>
              </TraceLine>
            </TraceCollapsibleTrigger>
            <TraceCollapsibleContent className="m-0 border-0 p-0">
              <ul>{done.map((task) => <TaskRow key={task.id} task={task} {...controls} />)}</ul>
            </TraceCollapsibleContent>
          </TraceCollapsible>
        </li> : null}
      </ul>
    </Trace>
  );
}
